import { useState, useEffect, useMemo, useCallback, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { LobbyTableInfo } from '@draughts/shared';
import { useTranslation } from 'react-i18next';
import { X, Settings2 } from 'lucide-react';
import {
  ACTIVE_VARIANTS,
  QUICK_TIME_CONTROLS,
  getVariantString,
  type ChallengeSettings,
  type GameVariant,
  type TimeControlPreset,
} from '@draughts/shared';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import { useMultiplayerStore } from '../../store/multiplayer-store';
import { useTournamentStore } from '../../store/tournament-store';
import { useArenaStore } from '../../store/arena-store';
import { garaInCorso, laMiaArenaEAttiva } from '../../utils/gara-in-corso';
import { useSenzaRete } from '../../hooks/useSenzaRete';
import { useLobbyStore } from '../../store/lobby-store';
import { useOnlineStore } from '../../store/online-store';
import { usePreferencesStore } from '../../store/preferences-store';
import { useAuthStore } from '../../store/auth-store';
import { listLobby } from '../../services/socket';
import { getLastVariant, setLastVariant } from '../../utils/last-used-play';
import { useVarianteDiGioca } from '../../store/variante-di-gioca-store';
import { variantPill } from '../../utils/variant-styling';
import { variantShortLabel } from '../../utils/variant-labels';
import { CADENCE_ICONS, parseTimeControl } from '../../utils/cadence-icons';
import { trackTableCreated, trackTableCancelled, trackTableSeated } from '../../utils/analytics';
import { log } from '../../utils/logger';
import VariantSelect from '../profile/VariantSelect';
import LobbyTableList from './LobbyTableList';
import CustomTableHost from './CustomTableHost';
import { cePannelloAperto } from '../game/FullscreenPanel';
import type { AIDifficulty } from '../../utils/ai-difficulty';
import type { AIGameOptions } from '../dashboard/DashboardPlayAIModal';
import '../dashboard/PlayModals.css';
// Le righe dei menu se ne sono andate col foglio della personalizzata, ma questo
// file porta anche il `.modal-overlay` a z-index 10000 su cui poggia il lobby.
import '../online/challenge-form.css';
import './PlayOverlay.css';
import { useModaleAccessibile } from '../../hooks/useModaleAccessibile';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Avvia una partita in presenza (due persone, questo dispositivo) con le opzioni
   * della partita personalizzata. Solo App puo' cambiare schermata, quindi arriva
   * da li'; senza, la voce "in presenza" non viene nemmeno offerta.
   */
  onPlayInPerson?: (settings: ChallengeSettings) => void;
  /**
   * Le altre tre destinazioni della partita personalizzata: il computer, un
   * giocatore scelto, un link da mandare a un amico. Come `onPlayInPerson`, solo
   * App puo' cambiare schermata o parlare col socket delle sfide, quindi arrivano
   * da fuori; senza, la voce corrispondente non viene offerta.
   */
  onPlayAI?: (variant: GameVariant, difficulty: AIDifficulty, options: AIGameOptions) => void;
  /** `null` se la sfida e' partita, altrimenti la CHIAVE i18n del motivo del no. */
  onSendChallenge?: (userId: string, settings: ChallengeSettings) => Promise<string | null>;
  onCreateChallengeLink?: (settings: ChallengeSettings) => void;
  /** Apre le tabelle di apertura dentro l'app invece che nel browser. */
  onOpenOpeningTables?: (variant: string) => void;
  /**
   * Apre il modale d'accesso, per l'ospite che sceglie la Sfida diretta. Passa
   * DRITTA: il modale sale da se' sopra i fogli, e chiuderli prima vorrebbe dire
   * chiudere due overlay nello stesso commit.
   */
  onSignIn?: () => void;
  /**
   * La variante con cui il foglio si apre: quella scelta nella card dei
   * punteggi, che è la scelta che l'utente ha appena fatto e vede.
   *
   * Serve una prop, e non basta la chiave persistita che l'inizializzatore
   * legge: questo foglio resta MONTATO anche da chiuso (il `return null` sta
   * sotto gli hook), quindi quello stato si semina una volta sola per tutta la
   * vita della schermata. Cambiare variante nella card scriveva la chiave e non
   * rientrava mai qui: si premeva Gioca e il menu mostrava la variante di
   * prima, finché non si ricaricava la pagina.
   */
  defaultVariant?: string;
  /**
   * Il secondo pannello, accanto a questo dentro lo STESSO velo: sul desktop e' la
   * lista di chi e' collegato (`ConnectedUsersModal affiancato`), che nella card dei
   * punteggi aveva una pastiglia sua e qui non serve piu'.
   *
   * Un velo e non due, perche' due veli vogliono dire due trappole del fuoco: quella
   * del pannello accanto vede il fuoco «fuori» da se' e a ogni TAB se lo strappa.
   * Quindi il dialogo diventa l'INVOLUCRO dei due — `.coppia-gioca` — e sono suoi il
   * ruolo, il nome accessibile, il fuoco e l'unica voce sullo stack del gesto
   * «indietro». Il pannello ospite li spegne tutti (vedi la sua prop `affiancato`).
   *
   * Una FUNZIONE e non un nodo, e riceve la variante che questo menu sta guardando:
   * i punteggi della lista accanto sono otto per giocatore, e quello giusto e'
   * quello della partita che si sta per fare. Finche' era un nodo, chi lo costruiva
   * — la card dei punteggi, o le preferenze dalla barra — non aveva modo di sapere
   * cosa si fosse scelto QUI, e le due meta' della stessa card dicevano due
   * varianti diverse: a sinistra «Italiana», a destra i numeri dell'internazionale.
   *
   * La variante resta una sola e sta qui (`variant`, col suo seme e la sua
   * persistenza): una callback `onVarianteCambiata` avrebbe aperto una seconda
   * verita' in ogni chiamante, da tenere allineata a mano.
   *
   * La sua PRESENZA e' anche l'interruttore «siamo affiancati», ed e' per questo che
   * le guardie qui sotto guardano la prop e non il nodo che torna: il giorno in cui
   * tornasse `null` la coppia resterebbe una coppia, con la sua X sola e il suo
   * dialogo solo.
   */
  pannelloAccanto?: (variante: string) => ReactNode;
}

/** Lowercase-canonical variant ids, in the app's canonical display order. */
const VARIANT_VALUES: string[] = ACTIVE_VARIANTS.map(getVariantString);

/**
 * "Gioca" — the lobby.
 *
 * Replaces the two-dropdown modal plus the spinner screen behind it. Instead of
 * disappearing into an invisible queue, pressing a cadence publishes a TABLE:
 * you either sit at the oldest one waiting or open your own, and either way the
 * whole site can see it. The list at the bottom is that queue, and clicking any
 * row is the same thing as pressing the button that would have handed it to you.
 */
/*
 * Costante di MODULO e non un `[]` scritto dentro il selettore: un letterale
 * nuovo a ogni chiamata e' un riferimento nuovo, cioe' esattamente il risveglio
 * che si vuole togliere.
 */
const VUOTO: LobbyTableInfo[] = [];


/**
 * Su che variante si apre il foglio: l'ultima partita giocata dall'account, o
 * la scelta fatta qui dopo (`useVarianteDiGioca`); senza account la memoria di
 * questo browser, come prima; poi la variante passata da chi apre, la
 * preferita, l'internazionale.
 */
function semeVariante(
  userId: string | null,
  defaultVariant: string | undefined,
  preferredVariant: string | undefined,
): string {
  const proposta = userId ? useVarianteDiGioca.getState().proposta(userId) : getLastVariant();
  return proposta ?? defaultVariant ?? preferredVariant ?? 'international';
}

export default function PlayOverlay({
  isOpen,
  onClose,
  onPlayInPerson,
  onPlayAI,
  onSendChallenge,
  onCreateChallengeLink,
  onOpenOpeningTables,
  onSignIn,
  defaultVariant,
  pannelloAccanto,
}: Props) {
  const { t } = useTranslation();
  const preferredVariant = usePreferencesStore((s) => s.preferredVariant);
  const isGuest = useAuthStore((s) => s.isGuest);
  const userId = useAuthStore((s) => s.user?.id ?? null);

  /*
   * La lista dei tavoli solo da APERTO.
   *
   * `PlayOverlay` e' montato sempre — la dashboard lo rende accanto alle card e
   * lui torna `null` da chiuso — quindi un abbonamento a `s.tables` lo
   * risvegliava a ogni annuncio della lobby anche mentre non disegnava niente, e
   * la lobby parla da sola perche' i bot di presenza aprono e ritirano tavoli.
   * Gli effetti sul socket erano gia' protetti da `isOpen`; questo era il buco
   * rimasto.
   */
  const tables = useLobbyStore((s) => (isOpen ? s.tables : VUOTO));
  const myTableId = useLobbyStore((s) => s.myTableId);
  const busy = useLobbyStore((s) => s.isBusy);
  const error = useLobbyStore((s) => s.error);

  const createQuickTable = useMultiplayerStore((s) => s.createQuickTable);
  const sitAtLobbyTable = useMultiplayerStore((s) => s.sitAtLobbyTable);
  const cancelMyTable = useMultiplayerStore((s) => s.cancelMyTable);
  const connect = useMultiplayerStore((s) => s.connect);
  const mpStatus = useMultiplayerStore((s) => s.status);
  /*
   * «I tavoli che vedi non esistono piu'.» La alza il multiplayer-store quando
   * il socket e' giu' da abbastanza o quando il dispositivo dichiara di non
   * avere rete, e lo store ha gia' svuotato la lista: qui serve a dire perche'
   * e' vuota, e a non promettere gesti che non si possono fare.
   */
  const presenzaScaduta = useMultiplayerStore((s) => s.presenzaScaduta);
  /*
   * «Sei in una gara»: il rifiuto che il server dara' comunque, detto PRIMA.
   *
   * Il server chiude la lobby a chi e' dentro un torneo o un'arena in corso
   * (`competition-guard.ts`), e lo fa da sempre: senza questa riga il rifiuto
   * arriva dopo il giro di rete, come errore sotto la lista, cioe' un bottone
   * che promette un tavolo e consegna una scusa. E' lo stesso difetto che la
   * nota accanto a `presenzaScaduta` descrive qui sotto.
   *
   * Resta una CORTESIA, non una guardia: il client non puo' sapere quanto il
   * server, che incrocia socket e utente su piu' dispositivi. Se i due non
   * sono d'accordo vince il rifiuto del server, che continua ad arrivare e a
   * tradursi in `lobby.errors.inCompetition` — la stessa frase che si legge
   * qui.
   */
  const torneoIscritto = useTournamentStore((s) => s.isJoined);
  const torneoStato = useTournamentStore((s) => s.status);
  const arenaIscrittaId = useArenaStore((s) => s.arenaIscrittaId);
  const arenaStato = useArenaStore((s) => s.status);
  const arenaInPausa = useArenaStore((s) => s.myParticipant?.status === 'paused');
  /* Scalare come gli altri, benche' legga la lista delle arene: vedi la nota
     accanto alla stessa riga in `BandaGaraInCorso`. */
  const arenaIscrittaAttiva = useArenaStore(laMiaArenaEAttiva);
  const inGara = garaInCorso({
    torneoIscritto, torneoStato, arenaIscrittaId, arenaStato, arenaInPausa, arenaIscrittaAttiva,
  }) !== null;
  const senzaRete = useSenzaRete();

  const [variant, setVariant] = useState<string>(() => semeVariante(userId, defaultVariant, preferredVariant));
  /* L'utente ha toccato il menu della variante in QUESTA apertura: la risposta
     del server, se arriva dopo, non deve scavalcare la sua scelta. */
  const toccata = useRef(false);
  const [customOpen, setCustomOpen] = useState(false);
  /**
   * A guest about to sit at a rated table. The game WILL be played — it just
   * will not count — and saying so before the board appears is the difference
   * between a rule and a surprise.
   */
  const [guestConfirmTableId, setGuestConfirmTableId] = useState<string | null>(null);

  /*
   * Ri-semina a ogni APERTURA, come fa il foglio della personalizzata: è lo
   * stesso difetto e la stessa cura.
   *
   * Il seme e' la variante dell'ULTIMA PARTITA GIOCATA dall'account, o quella
   * scelta qui dentro dopo (`useVarianteDiGioca`): non piu' l'ultima variante
   * toccata nel browser, che cambiava anche solo guardando un punteggio in
   * home. La prop resta il ripiego per chi non ha ancora giocato.
   */
  useEffect(() => {
    if (!isOpen) return;
    toccata.current = false;
    const seme = () => semeVariante(userId, defaultVariant, preferredVariant);
    setVariant(seme());
    /* E si rilegge l'ultima partita: puo' essere finita un minuto fa, o su un
       altro dispositivo. Arriva dopo l'apertura, quindi si applica solo se nel
       frattempo il menu non e' stato toccato. */
    let viva = true;
    void useVarianteDiGioca.getState().aggiorna(userId).then(() => {
      if (viva && !toccata.current) setVariant(seme());
    });
    return () => { viva = false; };
  }, [isOpen, defaultVariant, preferredVariant, userId]);

  useOverlayBackButton(isOpen, onClose);

  // Esc to close, like every other play modal — unless the custom sheet is
  // open on top of us: there Esc belongs to IT, one layer at a time.
  //
  // `cePannelloAperto()` e' la stessa regola per gli strati che NON sono nostri:
  // il pannello dei collegati qui accanto apre il profilo di un giocatore
  // (`FullscreenPanel`), e senza questa domanda un Escape chiudeva il profilo E la
  // coppia sotto. Si chiama DENTRO il gestore, non nelle dipendenze: la pila va
  // letta al momento del tasto, non al montaggio dell'effetto.
  useEffect(() => {
    if (!isOpen || customOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || cePannelloAperto()) return;
      onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, customOpen, onClose]);

  // The socket is what feeds the list; without it the overlay would open onto
  // an empty lobby that never fills in.
  useEffect(() => {
    if (!isOpen) return;
    if (mpStatus === 'disconnected') void connect();
  }, [isOpen, mpStatus, connect]);

  // One snapshot per opening; the table-added/removed broadcasts keep it live
  // from there.
  useEffect(() => {
    if (!isOpen) return;
    if (mpStatus === 'disconnected' || mpStatus === 'connecting') return;
    let cancelled = false;
    listLobby()
      .then((snapshot) => {
        if (cancelled) return;
        useLobbyStore.getState().setSnapshot(snapshot.tables, snapshot.myTableId);
        useOnlineStore.getState().setCounts(snapshot.counts);
      })
      .catch((err) => log.warn('PlayOverlay', 'lobby:list failed', { err }));
    return () => { cancelled = true; };
  }, [isOpen, mpStatus]);

  const myTable = useMemo(() => tables.find((tbl) => tbl.id === myTableId) ?? null, [tables, myTableId]);

  /*
   * La variante che questo menu sta GUARDANDO, che non sempre e' quella del suo
   * selettore: mentre si aspetta al proprio tavolo il selettore non e' a schermo
   * (vedi `myTable ? null :` nel corpo), e la variante che l'utente vede — e sta
   * aspettando — e' quella del tavolo che ha aperto.
   *
   * Una sola espressione per i due posti che ne hanno bisogno: i tavoli che vanno
   * in cima alla lista qui sotto, e i punteggi del pannello accanto. Erano la
   * stessa frase scritta due volte, ed e' il genere di coppia che divergerebbe.
   */
  const varianteGuardata = myTable ? myTable.variant : variant;

  const handleQuick = useCallback(
    (timeControl: TimeControlPreset) => {
      log.info('PlayOverlay', 'quick pressed', { variant, timeControl });
      trackTableCreated(variant, timeControl);
      // The picker's variant travels WITH the request. It used to be read off
      // the multiplayer store instead, which held whatever the last game left
      // there — so a table opened as Italian came out International.
      void createQuickTable(variant, timeControl);
    },
    [createQuickTable, variant],
  );

  const handleSit = useCallback(
    (tableId: string) => {
      const table = tables.find((tbl) => tbl.id === tableId);
      // Only an 'any' table needs the warning: it lets the guest in and then
      // does not count. A "solo valida" one never gets here — its seat is
      // disabled in the row — and a casual one has nothing to warn about.
      if (isGuest && (table?.matchType ?? (table?.rated ? 'any' : 'casual')) === 'any') {
        setGuestConfirmTableId(tableId);
        return;
      }
      trackTableSeated(table?.variant ?? variant, table?.timeControl ?? 'unknown');
      void sitAtLobbyTable(tableId);
    },
    [isGuest, sitAtLobbyTable, tables, variant],
  );

  const confirmGuestSit = useCallback(() => {
    const tableId = guestConfirmTableId;
    setGuestConfirmTableId(null);
    if (!tableId) return;
    const table = tables.find((tbl) => tbl.id === tableId);
    trackTableSeated(table?.variant ?? variant, table?.timeControl ?? 'unknown');
    void sitAtLobbyTable(tableId);
  }, [guestConfirmTableId, sitAtLobbyTable, tables, variant]);

  const handleCancel = useCallback(() => {
    trackTableCancelled();
    cancelMyTable();
  }, [cancelMyTable]);

  const handleOpenOpeningTables = useCallback(
    (variant: string) => {
      onOpenOpeningTables?.(variant);
      onClose();
    },
    [onClose, onOpenOpeningTables],
  );

  /*
   * Il fuoco dentro il dialogo, Tab che non ne esce, e alla chiusura il fuoco
   * torna a chi l'aveva: senza, la tabulazione continua nella pagina SOTTO, che
   * è ancora lì dietro il velo. Senza `onClose` perché l'Escape lo gestisce già
   * il componente. Vedi `useModaleAccessibile`.
   */
  const riferimentoDialogo = useModaleAccessibile<HTMLDivElement>(isOpen);

  if (!isOpen) return null;

  const cadenceLabel = (tc: string): string => parseTimeControl(tc)?.clock ?? tc;

  /* `play-online-modal` is the shared play-sheet skin — the same one Sfida AI
     wears (see PlayModals.css). It carries the full-screen sheet at ≤767px, the
     sticky header, the plain surface and the desktop card, so the three play
     surfaces stay one look instead of three. */
  const corpoGioca = (
    <>
      <div className="play-online-head">
        <h2>{t('lobby.title')}</h2>
        {/* Niente pillola «N Online» qui, di proposito. Stava fra il titolo e
            la X, ma chi arriva a questo foglio l'ha appena letta nella card
            della dashboard, sotto «Gioca subito», e non dice la sola cosa che
            qui conta: se premendo una cadenza si gioca subito. Con pochi
            collegati — per lo piu' bot di presenza contati fra gli ospiti —
            il numero diceva anzi il contrario, «qui non c'e' nessuno». */}
        {/* Da affiancati la X non sta qui: la coppia e' UNA card, e questa
            testata ne e' la meta' sinistra — la X cadrebbe in mezzo, sul filo
            fra le due colonne, mentre la si cerca dove la si cerca sempre,
            nell'angolo in alto a destra. La porta la testata di destra
            (ConnectedUsersModal), che quell'angolo e'. Resta una X sola, e
            chiude la coppia: `onClose` di la' e' lo stesso di qua.
            Fuori dal DOM e non spenta con `display: none`: un bottone spento
            resterebbe nel giro del TAB — e' la stessa ragione per cui sotto i
            768px la coppia non si costruisce affatto. */}
        {!pannelloAccanto && (
          <button type="button" className="play-online-close" aria-label={t('common.cancel')} onClick={onClose}>
            <X size={20} aria-hidden="true" />
          </button>
        )}
      </div>

      <div className="play-online-body">

        {/* Mentre si aspetta al proprio tavolo qui non c'e' niente: il
            tavolo lo mostra la lista, prima riga e fissa. C'era una card che
            diceva le stesse cose con lo stesso «Annulla», mentre la riga
            stava in fondo alla coda — lo stesso tavolo disegnato due volte.
            Il menu sparisce lo stesso: si sta in coda in un posto alla
            volta, e il server tiene un solo tavolo vivo per utente. */}
        {myTable ? null : (
          <>
            <div className="modal-field">
              <span className="modal-label">{t('home.variant')}</span>
              <VariantSelect
                value={variant}
                onChange={(v) => {
                  toccata.current = true;
                  setVariant(v);
                  /* La scelta fatta qui vale fino alla prossima partita giocata. */
                  useVarianteDiGioca.getState().scegli(userId, v);
                  setLastVariant(v);
                }}
                variants={VARIANT_VALUES}
                preferred={preferredVariant}
                label={(v) => variantShortLabel(t, v)}
                dotColor={() => 'transparent'}
                renderLeading={(v) => variantPill(v).flag}
                ariaLabel={t('home.variant')}
              />
            </div>

            {/* Il motivo PRIMA dei bottoni grigi: un bottone spento, da solo,
                invita il tocco che una frase non invita. La chiave e' quella
                del rifiuto del server, gia' tradotta in otto lingue — cosi' chi
                prova lo stesso da un'altra scheda legge le stesse parole. */}
            {inGara && <p className="lobby-nota-gara" role="status">{t('lobby.errors.inCompetition')}</p>}

            {/* Le quattro cadenze rapide, nell'ordine di `QUICK_TIME_CONTROLS`.
                Una pressione = ci si siede al tavolo piu' vecchio che
                combacia, o si pubblica il proprio. */}
            <div className="lobby-quick" role="group" aria-label={t('lobby.quickPlay')}>
              {QUICK_TIME_CONTROLS.map((tc) => (
                <button
                  key={tc}
                  type="button"
                  className="lobby-quick-btn"
                  onClick={() => handleQuick(tc)}
                  /* Senza presenza il rifiuto si scopriva DOPO il click, come
                     `online.errors.nonConnesso` sotto la lista: un bottone che
                     promette un tavolo e consegna un errore. */
                  disabled={busy || presenzaScaduta || inGara}
                >
                  <span className="lobby-quick-ico" aria-hidden="true">
                    {CADENCE_ICONS[parseTimeControl(tc)?.category ?? 'blitz']}
                  </span>
                  <span className="lobby-quick-clock">{cadenceLabel(tc)}</span>
                </button>
              ))}
            </div>

            {/* Apre il foglio dedicato. Prima era un'espansione qui sotto:
                otto righe di menu infilate fra le cadenze e la lista dei
                tavoli, con "Apri il tavolo" spinto fuori vista. */}
            <button
              type="button"
              className="lobby-custom-toggle"
              aria-haspopup="dialog"
              onClick={() => setCustomOpen(true)}
              disabled={presenzaScaduta || inGara}
            >
              <Settings2 size={16} aria-hidden="true" />
              {t('lobby.customGame')}
            </button>
          </>
        )}

        {error && <p className="lobby-error" role="alert">{t(error)}</p>}

        {guestConfirmTableId && (
          <div className="lobby-guest-confirm" role="alertdialog">
            <p>{t('online.challenge.link.guestCasualNote')}</p>
            <div className="lobby-guest-confirm-actions">
              <button type="button" className="lobby-guest-confirm-cancel" onClick={() => setGuestConfirmTableId(null)}>
                {t('common.cancel')}
              </button>
              <button type="button" className="lobby-guest-confirm-ok" onClick={confirmGuestSit}>
                {t('lobby.sit')}
              </button>
            </div>
          </div>
        )}

        <LobbyTableList
          tables={tables}
          myTableId={myTableId}
          isGuest={isGuest}
          // La variante che porta in cima i suoi tavoli: vedi `varianteGuardata`.
          selectedVariant={varianteGuardata}
          busy={busy}
          presenzaScaduta={presenzaScaduta}
          senzaRete={senzaRete}
          onSit={handleSit}
          onCancel={handleCancel}
        />
      </div>
    </>
  );

  /*
   * Da solo: il foglio E' il dialogo, com'e' sempre stato.
   *
   * Affiancato: il dialogo diventa l'involucro dei due pannelli, e questo torna una
   * card e basta. Il ruolo, il nome, il fuoco e la voce di cronologia stanno in UN
   * posto solo — vedi `pannelloAccanto`.
   */
  const foglio = (
    <div
      className="modal dashboard-modal play-online-modal play-overlay"
      ref={pannelloAccanto ? undefined : riferimentoDialogo}
      role={pannelloAccanto ? undefined : 'dialog'}
      aria-modal={pannelloAccanto ? undefined : true}
      tabIndex={pannelloAccanto ? undefined : -1}
      aria-label={pannelloAccanto ? undefined : t('lobby.title')}
      onClick={(e) => e.stopPropagation()}
    >
      {corpoGioca}
    </div>
  );

  /*
   * Il velo della coppia esce da `.dash` con un portale, e non e' un vezzo.
   *
   * La home e' un contesto d'impilamento (`.dash-bg-wrap > .dash` e `.dash > *`
   * stanno a `z-index: 1`), quindi il 9000 del velo vale solo LI' DENTRO: la barra
   * laterale, `position: fixed` a `z-index: 100` e larga `--sidebar-width`, gli
   * passa sopra. Col foglio solo non si vedeva — 560px centrati non arrivano mai
   * sotto gli 80px di sinistra — ma la coppia e' larga fino a 1096 e ci finisce
   * sotto a quasi ogni larghezza. Il portale e' la stessa strada della conferma di
   * uscita in HeroPanel e dei pannelli a tutto schermo.
   * Solo il ramo affiancato: il foglio solo resta dov'e', e con lui le prove che lo
   * cercano dentro il container di render.
   */
  const velo = (
    /* Qualunque tocco o tasto dentro il foglio vuol dire che l'utente ci sta
       lavorando: da li' la risposta del server sull'ultima partita non cambia
       piu' la variante (non sotto il dito, non a tendina aperta). */
    <div
      className="modal-overlay"
      onClick={onClose}
      onPointerDownCapture={() => { toccata.current = true; }}
      onKeyDownCapture={() => { toccata.current = true; }}
    >
      {pannelloAccanto ? (
        <div
          className="coppia-gioca"
          ref={riferimentoDialogo}
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
          aria-label={t('lobby.title')}
          onClick={(e) => e.stopPropagation()}
        >
          {foglio}
          {pannelloAccanto(varianteGuardata)}
        </div>
      ) : (
        foglio
      )}
    </div>
  );

  const tutto = (
    <>
      {velo}

      {/* Fratello, non figlio: annidato dentro il fondale di Gioca, ogni click
          al suo interno risalirebbe fino a `onClose` e chiuderebbe entrambi.
          Stesso z-index, quindi è l'ordine nel DOM a metterlo sopra — la stessa
          strada che ConnectedUsersModal fa da DashboardPage. */}
      <CustomTableHost
        isOpen={customOpen}
        onClose={() => setCustomOpen(false)}
        defaultVariant={variant}
        // Ogni avvio che porta altrove chiude anche il lobby: questa istanza puo'
        // essere quella GLOBALE, che resta montata sopra la schermata di gioco.
        onLaunched={onClose}
        onPlayInPerson={onPlayInPerson}
        onPlayAI={onPlayAI}
        onSendChallenge={onSendChallenge}
        onCreateChallengeLink={onCreateChallengeLink}
        onOpenOpeningTables={onOpenOpeningTables ? handleOpenOpeningTables : undefined}
        onSignIn={onSignIn}
      />
    </>
  );

  /*
   * Da affiancati esce TUTTO dal portale, velo e sfoglia insieme — e «insieme» e'
   * la parte che conta.
   *
   * Il portale serve al velo: la home e' un contesto d'impilamento (`.dash` sta a
   * `z-index: 1`), quindi li' dentro il 9000 del velo non supera la barra laterale,
   * che e' `fixed` a 100 e larga `--sidebar-width`. Col foglio solo non si vedeva —
   * 560px centrati non arrivano mai sotto gli 80 di sinistra — con la coppia, larga
   * fino a 1096, si'.
   *
   * Ma portare fuori il SOLO velo rompeva la riga qui sopra: «stesso z-index, quindi
   * e' l'ordine nel DOM a metterlo sopra». Il velo finiva in fondo a `body` e la
   * sfoglia restava dentro `.dash`, cioe' SOTTO — e «Partita personalizzata», aperta
   * dalla coppia, si apriva dietro di lei. Fuori tutti e due, l'ordine dei fratelli
   * torna quello scritto e la sfoglia torna sopra.
   *
   * Figli DIRETTI di `body`, e non di un contenitore nostro: `#root` e i figli di
   * body che non sono lui sono gli unici coperti dal `touch-action` che spegne lo
   * zoom nativo (index.css, e useBloccoLente.cablaggio.test.ts).
   */
  return pannelloAccanto ? createPortal(
    tutto,
    document.body,
  ) : tutto;
}
